export const POPAD_COVERAGE_BANDS = [25, 50, 75, 100] as const;

export interface LimitedCapacityFormat {
  id: number;
  name: string;
  capacityType?: string | null;
  bookingType?: string | null;
  bookingMeta?: {
    bands?: number[];
    absoluteLimitPerStore?: number;
  } | null;
}

export function quantityToCoveragePercent(quantity: number, capacity: number): number {
  if (capacity <= 0) return 0;
  return Math.round((quantity / capacity) * 100);
}

export function coverageToQuantity(coveragePercent: number, capacity: number): number {
  if (capacity <= 0 || coveragePercent <= 0) return 0;
  return Math.floor((coveragePercent / 100) * capacity);
}

export function nearestPopadCoverageBand(
  coveragePercent: number,
  bands: readonly number[] = POPAD_COVERAGE_BANDS,
): number {
  return bands.reduce((prev, curr) =>
    Math.abs(curr - coveragePercent) < Math.abs(prev - coveragePercent) ? curr : prev,
  );
}

export function getMaxPopadUnits(params: {
  capacity: number;
  currentQuantity: number;
  remainingUnits: number;
}): number {
  return params.remainingUnits + params.currentQuantity;
}

export function getMaxLimitedCapacityUnits(params: {
  capacity: number;
  currentQuantity: number;
  remainingUnits: number;
  absoluteLimitPerStore?: number | null;
}): number {
  const creditedMax = getMaxPopadUnits(params);
  if (params.absoluteLimitPerStore == null) return creditedMax;
  return Math.min(params.absoluteLimitPerStore, creditedMax);
}

export interface LimitedCapacityAvailabilityHint {
  bookedLine: string;
  availabilityLine: string;
}

export function formatLimitedCapacityAvailabilityHint(params: {
  capacity: number;
  currentQuantity: number;
  remainingUnits: number;
  maxUnits: number;
  includePercents?: boolean;
}): LimitedCapacityAvailabilityHint {
  const {
    capacity,
    currentQuantity,
    remainingUnits,
    maxUnits,
    includePercents = true,
  } = params;

  const bookedLine = includePercents && capacity > 0
    ? `${currentQuantity}/${capacity} Booked (${quantityToCoveragePercent(currentQuantity, capacity)}%)`
    : `${currentQuantity}/${capacity} Booked`;

  const availabilityLine = includePercents && capacity > 0
    ? `${remainingUnits} additional units free (${quantityToCoveragePercent(maxUnits, capacity)}% max)`
    : `${remainingUnits} additional units free (max ${maxUnits})`;

  return { bookedLine, availabilityLine };
}

export function getEditablePopadCoverageOptions(params: {
  capacity: number;
  currentQuantity: number;
  remainingUnits: number;
  bands?: readonly number[];
}): number[] {
  const { capacity, currentQuantity, remainingUnits, bands = POPAD_COVERAGE_BANDS } = params;
  if (capacity <= 0) return [0];

  const maxUnits = getMaxPopadUnits({ capacity, currentQuantity, remainingUnits });
  const currentCoverage = quantityToCoveragePercent(currentQuantity, capacity);
  const currentBand = nearestPopadCoverageBand(currentCoverage, bands);

  const options = new Set<number>([0]);
  for (const band of bands) {
    if (coverageToQuantity(band, capacity) <= maxUnits) {
      options.add(band);
    }
  }

  if (currentQuantity > 0) {
    options.add(currentBand);
  }

  return Array.from(options).sort((a, b) => a - b);
}

export function canSelectPopadCoverage(params: {
  coveragePercent: number;
  capacity: number;
  currentQuantity: number;
  remainingUnits: number;
}): boolean {
  if (params.coveragePercent === 0) return true;
  const maxUnits = getMaxPopadUnits(params);
  return coverageToQuantity(params.coveragePercent, params.capacity) <= maxUnits;
}

export function parsePopadFormatsResponse(data: unknown): LimitedCapacityFormat[] {
  const formats = Array.isArray(data)
    ? data
    : (data && typeof data === 'object' && Array.isArray((data as { formats?: unknown }).formats)
      ? (data as { formats: unknown[] }).formats
      : []);

  return formats.filter((item): item is LimitedCapacityFormat =>
    item != null
    && typeof item === 'object'
    && typeof (item as { id?: unknown }).id === 'number'
    && typeof (item as { name?: unknown }).name === 'string',
  );
}
